import { VA_INITIAL_FREE_YEAR_ID } from 'Utils/Constants';
import { logger } from 'Utils/Logger';
import {
  addGiftPlanInfo,
  getPlansWithDppContent,
  handleGeneralPlans,
  handlePlanToSelect,
  handleSheerIdPlans,
  handleVeteransAdvPlans,
  mapPackages,
} from 'Utils/Plans';
import { shouldGetSheerIdPlans } from 'Utils/SheerIdHelpers';
import { getMonthlyExperimentPlans, getSetMonthly30DayFreeTrialVariant } from 'Utils/Analytics/Amplitude';
import { VeteransAdvStatus, shouldGetVAPlans } from 'Utils/VeteransAdvHelpers';
import { getState, setState } from 'Utils/Store';
import { clearSheerId, clearVeteransAdvError, clearVeteransAdvId } from 'State/Integrations';
import { getUserType } from 'Utils/Dpp';

export const plansInitialState = {
  isFetchingPlans: false,
  isPlanSelected: false,
  allPlans: [],
  getPlansSuccess: false,
  selectedPlan: null,
  selectedPlanPrice: 0,
  selectedPlanId: null,
  selectPlanActionComplete: false,
  willFetchVeteransAdvAfterSignup: false,
};

// action creators
export const fetchPlansRequest = (payload) => setState('plans', { ...payload, isFetchingPlans: true });
export const fetchPlans = (payload) => setState('plans', { ...payload, getPlansSuccess: true, isFetchingPlans: false });
export const setSelectedPlan = (payload) =>
  setState('plans', { ...payload, isPlanSelected: true, selectPlanActionComplete: true });
export const clearPlans = () => setState('plans', plansInitialState);
export const clearSelectedPlan = () =>
  setState('plans', {
    isPlanSelected: plansInitialState.isPlanSelected,
    selectedPlan: plansInitialState.selectedPlan,
    selectedPlanPrice: plansInitialState.selectedPlanPrice,
    selectedPlanId: plansInitialState.selectedPlanId,
    selectPlanActionComplete: true,
  });
export const willFetchVeteransAdvAfterSignup = (payload) =>
  setState('plans', { willFetchVeteransAdvAfterSignup: !!payload });

// action functions
export const selectPlan = (planId = '', plans) => {
  const {
    plans: { allPlans, selectedPlanId },
    appConfig: {
      defaultConfig: { defaultPackage },
    },
    purchaseMode: { requirePlanSelection },
  } = getState();
  const planChoices = plans || allPlans;
  logger.started('select plan', { planId });
  const defaultSelectedPlan = requirePlanSelection ? '' : defaultPackage;
  const serviceId = planId || selectedPlanId || defaultSelectedPlan;
  const selectedPlan = planChoices.find(({ packageID }) => packageID === serviceId) || null;
  if (selectedPlan) {
    logger.completed('select plan', { planId, selectedPlan, selectedPlanId });

    setSelectedPlan({
      selectedPlan,
      selectedPlanId: serviceId,
      selectedPlanPrice: selectedPlan.planPrice || 0,
    });
  } else {
    const error = `Plan not found for planId: '${serviceId}'`;
    logger.error('select plan', { selectedPlan, selectedPlanId, requirePlanSelection, serviceId }, error);
  }
};

export const selectRestartPlan = (selectedPlan) => {
  logger.started('select restart plan', { selectedPlan });
  setSelectedPlan({ selectedPlan });
  logger.completed('select restart plan', { selectedPlan });
};

export const getPlans = async (preselectedPlan) => {
  logger.started('get plans');
  fetchPlansRequest();

  const {
    user: { isAuthenticated },
    plans: { selectedPlanId, willFetchVeteransAdvAfterSignup: veteransAdvAfterSignup },
    subscription,
    integrations: {
      bango,
      veteransAdv: { member_id, error: vaError },
    },
  } = getState();

  let planToSelect = (preselectedPlan || selectedPlanId || '').toUpperCase();

  let getVeteransAdvPlans;
  if (vaError) {
    getVeteransAdvPlans = VeteransAdvStatus.ERROR;
  } else if (member_id) {
    getVeteransAdvPlans = VeteransAdvStatus.VALID;
  } else {
    getVeteransAdvPlans = await shouldGetVAPlans(isAuthenticated, subscription, bango);
  }
  const getSheerIdPlans = await shouldGetSheerIdPlans(isAuthenticated);

  let plans = {
    backendPlans: [],
    dppPlans: [],
  };

  if (getVeteransAdvPlans === VeteransAdvStatus.VALID) {
    clearVeteransAdvError();

    plans = await handleVeteransAdvPlans(subscription);
    if (plans.backendPlans.length > 0) {
      if (!planToSelect) planToSelect = VA_INITIAL_FREE_YEAR_ID;

      /* if not getting any plans (most likely due to an error) then just offer general market plans instead */
    } else {
      clearVeteransAdvId();
      plans = await handleGeneralPlans(subscription, planToSelect);
    }
  } else if (getSheerIdPlans) {
    plans = await handleSheerIdPlans(subscription);
    /* if not getting any plans (most likely due to an error) then just offer general market plans instead */
    if (!plans.backendPlans.length) {
      clearSheerId();
      plans = await handleGeneralPlans(subscription, planToSelect);
    }
  } else {
    plans = await handleGeneralPlans(subscription, planToSelect);
  }

  if (veteransAdvAfterSignup) {
    // because this is a weird case where user enters the payment page directly without selecting a plan
    //    beforehand, normally they're redirected back to plan selection. so we need to set a flag in the
    //    store so the payment page waits for the veterans adv to be fetched and selected
    // regardless of whether or not VA plan was successfully fetched/selected above, it is the VA query param
    //    that set this flag initially, so we should disable it here
    willFetchVeteransAdvAfterSignup(false);
  }

  const userType = getUserType(subscription.hasActiveSubscription, subscription.isChurnedUser);
  const mappedBackendPlans = mapPackages(plans.backendPlans, plans.dppPlans, userType);
  plans = {
    ...plans,
    backendPlans: mappedBackendPlans,
  };

  if (subscription.purchaseType === 'gift') {
    plans = addGiftPlanInfo(plans, subscription);
  }

  //Merging plan info from BE with plan info from DPP
  let allPlans = getPlansWithDppContent(plans);

  const monthExperimentVariant = getSetMonthly30DayFreeTrialVariant();
  // for the Monthly 30 Day Free Trial experiment
  allPlans = getMonthlyExperimentPlans(monthExperimentVariant, allPlans);

  //finding plan to select
  const planId = handlePlanToSelect(
    planToSelect,
    allPlans,
    subscription.activeSubscriptionId,
    subscription.nextSubscription
  );

  fetchPlans({ allPlans });
  if (planId) {
    selectPlan(planId, allPlans);
  } else {
    clearSelectedPlan();
  }

  logger.completed('get plans', { allPlans, planToSelect: planId });
};
